import { useTranslation } from '@pypestream/translations';
import { useEffect, useRef, useState } from 'react';
import { proStudioUrqlGql } from '@pypestream/api-services';
import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import {
  buildPublishAttemptId,
  notifyPublishOutcome,
  PUBLISH_IN_PROGRESS_TOAST_ID,
  type SolutionVersionStatus,
} from '../utils/publish-status';
import {
  capitalizeWords,
  readDeploymentAuthorName,
  trackPublishProgress,
} from '../utils';
import { sendUserMessage, updateUserMessage } from '../xstate/app.xstate-utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';

/**
 * The backend's publish lock expires after this long (`LOCK_TIMEOUT_MS` in
 * `acquire-publish-lock.ts`): a PUBLISHING status older than that is a publish the backend
 * itself no longer honours, so it must not keep the Publish button disabled either.
 */
export const PUBLISH_LOCK_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * The solution's publish status as "is a publish running", with the backend's own lock
 * expiry applied — a lock nobody released is not a running publish.
 */
export const readSolutionPublishStatus = (
  row: { status?: unknown; publishStartedAt?: unknown } | null | undefined,
  now: number = Date.now()
): SolutionVersionStatus | null => {
  if (!row) return null;
  if (row.status !== 'PUBLISHING') return 'ACTIVE';
  const startedAt =
    typeof row.publishStartedAt === 'string'
      ? Date.parse(row.publishStartedAt)
      : NaN;
  if (
    Number.isFinite(startedAt) &&
    now - startedAt >= PUBLISH_LOCK_TIMEOUT_MS
  ) {
    return 'ACTIVE';
  }
  return 'PUBLISHING';
};

/**
 * Everyone's view of a publish: the Publish button disabled while one runs, and a toast
 * that says who is publishing what, where, and which step it is on — then how it ended.
 *
 * Draft-only versioning (backend `docs/solution-versioning-redesign.md`): publishing
 * snapshots the draft onto a separate version slot and never touches the draft, so the
 * editor stays EDITABLE throughout — only the Publish button is disabled, for everyone,
 * so two publishes cannot run at once. Nothing here forces view mode any more.
 *
 * Two sources, deliberately:
 *
 * - the SOLUTION's status (`PUBLISHING` while the backend holds its publish lock) says a
 *   publish is running. It flips the instant a publish or republish is accepted, before
 *   the deployment row exists. The draft version's own status is useless for this now: it
 *   never changes — the slot is what goes PUBLISHING.
 * - the deployment rows (`ctx.solutionDeployments`, fed by `useSolutionDeployments`) say
 *   how the publish is going and how it ended. The status cannot: it returns to `ACTIVE`
 *   on failure exactly as on success.
 *
 * The solution status is still the fallback when there is no deployment row at all, so a
 * solution whose rows are unreadable still gets its button back.
 *
 * Mounted once at the router, keyed on the open solution.
 */
export function usePublishStatusWatcherForCoworkers(): void {
  const [t] = useTranslation();
  const { solutionId, deployments, projectUsers, currentUserId } =
    useProStudioCtxSelector((ctx) => ({
      // The same key `useSolutionDeployments` subscribes on.
      solutionId: ctx.selectedBot?.uniqueId || null,
      deployments: ctx.solutionDeployments,
      projectUsers: ctx.projectUsers,
      currentUserId: ctx.userInfo?.id,
    }));

  const [versionStatus, setVersionStatus] =
    useState<SolutionVersionStatus | null>(null);

  /** True while this tab is showing the in-progress state, so it can announce the outcome. */
  const isShowingProgressRef = useRef(false);
  /** Last in-progress toast body, so a step change updates the toast instead of stacking one. */
  const progressTextRef = useRef<string | null>(null);
  /** The deployment row being followed — see `trackPublishProgress`. */
  const trackedDeploymentIdRef = useRef<string | undefined>(undefined);
  /**
   * `publishStartedAt` of the attempt in progress — it tells one attempt from the next when
   * there is no deployment row to deduplicate the announcement on.
   */
  const publishStartedAtRef = useRef<string | null>(null);
  /**
   * Whether this tab saw the solution go PUBLISHING. Required for the no-deployment-row
   * fallback: without it, a stale `ACTIVE` (e.g. carried over as the open solution changes)
   * would read as a publish that just finished.
   */
  const sawPublishingRef = useRef(false);

  useEffect(() => {
    if (!solutionId) {
      setVersionStatus(null);
      return undefined;
    }

    const { subscribe } = proStudioUrqlGql.solutionPublishStatusSubscription({
      id: solutionId,
    });
    const { unsubscribe } = subscribe((subData) => {
      if (subData.error) return;
      const row = subData.data?.tenancy_solutions_by_pk;
      if (row == null) return;
      if (typeof row.publishStartedAt === 'string') {
        publishStartedAtRef.current = row.publishStartedAt;
      }
      setVersionStatus(readSolutionPublishStatus(row));
    });

    return () => {
      unsubscribe();
      setVersionStatus(null);
      const wasShowingProgress = isShowingProgressRef.current;
      isShowingProgressRef.current = false;
      progressTextRef.current = null;
      trackedDeploymentIdRef.current = undefined;
      publishStartedAtRef.current = null;
      sawPublishingRef.current = false;
      // PSTUDIO-3985: undo what the in-progress branch raised. The button lock and the
      // `autoClose: false` banner are global while this subscription is scoped to one
      // solution, so a solution the backend leaves stuck in PUBLISHING would otherwise
      // carry them onto every solution opened afterwards. The toast is dismissed only when
      // it was raised, because this runs on every navigation; it reads the store flag too
      // because `PublishModal.onPublish` raises both before this subscription has seen
      // PUBLISHING.
      const wasPublishing =
        wasShowingProgress || tableStore.solutionVersionPublishingInProgress;
      tableStore.setSolutionVersionPublishingInProgress(false);
      if (wasPublishing) {
        dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
      }
    };
  }, [solutionId]);

  useEffect(() => {
    if (versionStatus === 'PUBLISHING') sawPublishingRef.current = true;

    const { phase, deployment, step, trackedDeploymentId } =
      trackPublishProgress({
        trackedDeploymentId: trackedDeploymentIdRef.current,
        versionStatus,
        // No version to prefer: the draft has no deployment rows of its own, and the slot
        // a publish runs on is only known from its row — the newest running one.
        solutionVersionId: null,
        deployments,
      });
    trackedDeploymentIdRef.current = trackedDeploymentId;
    const environment = capitalizeWords(deployment?.environment ?? '');
    const version = deployment?.versionName ?? '';
    const author = readDeploymentAuthorName(
      deployment?.deployedBy,
      projectUsers
    );
    const isSelf = Boolean(
      deployment?.deployedBy && deployment.deployedBy === currentUserId
    );

    if (phase === 'in_progress') {
      // Named header when a coworker is publishing — that is the fact that explains why the
      // Publish button just went disabled. Falls back to the impersonal wording when the row (and so
      // the environment, version and author) is not there yet.
      const header =
        version && environment && author && !isSelf
          ? (t('pro-studio/navigation:publish_modal.toast.publishing_by', {
              defaultValue: '{{name}} is publishing {{version}} to {{env}}',
              name: author,
              version,
              env: environment,
            }) ?? undefined)
          : version && environment
            ? (t('pro-studio/navigation:publish_modal.toast.publishing_to', {
                defaultValue: 'Publishing {{version}} to {{env}}',
                version,
                env: environment,
              }) ?? undefined)
            : (t(
                'pro-studio/navigation:publish_modal.toast.publishing_in_progress_header',
                {
                  defaultValue: 'Publishing is in progress.',
                }
              ) ?? undefined);

      // The in-flight status names the step the deploy is on, so it doubles as progress text.
      const text = step
        ? (t('pro-studio/navigation:publish_modal.toast.publishing_step', {
            defaultValue: '{{step}} — you can keep editing while it publishes.',
            step,
          }) ?? '')
        : (t(
            'pro-studio/navigation:publish_modal.toast.publishing_in_progress_text',
            {
              defaultValue:
                "You can keep editing. We'll let you know when the solution is published.",
            }
          ) ?? '');

      if (!isShowingProgressRef.current) {
        isShowingProgressRef.current = true;
        progressTextRef.current = text;
        // The button lock only. The draft is not being published — a snapshot of it is — so
        // the editor stays exactly as it was.
        tableStore.setSolutionVersionPublishingInProgress(true);
        sendUserMessage({
          type: 'info',
          header,
          text,
          toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
          autoClose: false,
        });
        return;
      }

      if (text !== progressTextRef.current) {
        progressTextRef.current = text;
        updateUserMessage({
          type: 'info',
          header,
          text,
          toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
          autoClose: false,
        });
      }
      return;
    }

    // Only report an outcome for a publish this tab actually saw running. Without this, the
    // last (already finished, or long-failed) deployment row would be announced on every
    // mount — the same trap the old `sawPublishing` guard existed for.
    if (!isShowingProgressRef.current) return;

    // `idle` means no attempt is being followed and the solution is no longer PUBLISHING. Only
    // a solution this tab watched go PUBLISHING and come back to ACTIVE counts as a finished
    // publish — the pre-deployment-rows fallback, for a solution whose rows never arrive.
    if (
      phase === 'idle' &&
      !(sawPublishingRef.current && versionStatus === 'ACTIVE')
    ) {
      return;
    }
    sawPublishingRef.current = false;

    isShowingProgressRef.current = false;
    progressTextRef.current = null;

    const attemptId = buildPublishAttemptId({
      deploymentId: deployment?.id,
      fallbackKey:
        solutionId && publishStartedAtRef.current
          ? `${solutionId}@${publishStartedAtRef.current}`
          : solutionId,
    });
    publishStartedAtRef.current = null;

    if (phase === 'failed') {
      const message =
        (step && version && environment
          ? t('pro-studio/navigation:publish_modal.toast.publish_failed_step', {
              defaultValue:
                'Publishing {{version}} to {{env}} failed at “{{step}}”. Publish again to retry with your current changes.',
              version,
              env: environment,
              step,
            })
          : version && environment
            ? t('pro-studio/navigation:publish_modal.toast.publish_failed_to', {
                defaultValue:
                  'Publishing {{version}} to {{env}} failed. Publish again to retry with your current changes.',
                version,
                env: environment,
              })
            : t('pro-studio/navigation:publish_modal.toast.publish_failed', {
                defaultValue:
                  'There was an error publishing this solution. Please reach out to your Pypestream point of contact for assistance',
              })) ?? '';

      notifyPublishOutcome({ attemptId, outcome: 'failure', message });
      return;
    }

    const message =
      t('pro-studio/navigation:publish_modal.toast.published_success', {
        defaultValue: 'The solution has been published.',
      }) ?? '';

    notifyPublishOutcome({ attemptId, outcome: 'success', message });
    // Refreshes the version list and the environment cards with the new slot. The draft's
    // identity does not move, so the table is not reloaded by this.
    sendProStudioEvent({ type: 'reloadCurrentBotData' });
  }, [deployments, versionStatus, solutionId, projectUsers, currentUserId, t]);
}
